import type { ICompanyAppMapping } from "@models";

export interface ICompanyApplicationMappingOption {
  /** Company application mapping id (`cc.cc_company_app_map.id`, OpenAPI appMapId). */
  appMapId: string;
  label: string;
}

function extractCompanyAppsMappingList(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw;
  if (
    raw != null &&
    typeof raw === "object" &&
    "content" in raw &&
    Array.isArray((raw as { content: unknown }).content)
  ) {
    return (raw as { content: unknown[] }).content;
  }
  return [];
}

/** Normalizes `GET /api/company/{uuid}/company-apps-mapping` (Applications tab). */
export function normalizeCompanyAppsMapping(
  raw: unknown,
): ICompanyAppMapping[] {
  return extractCompanyAppsMappingList(raw).map((item, index) => {
    const row = item as Record<string, unknown>;
    const rawId = row?.id;
    const id =
      rawId != null && (typeof rawId === "string" || typeof rawId === "number")
        ? String(rawId)
        : String(index);
    return {
      id,
      cloudProvider:
        (row?.cloudProvider as string) ?? (row?.cldPrvdrName as string) ?? "",
      region: (row?.region as string) ?? (row?.regionName as string) ?? "",
      application:
        (row?.application as string) ?? (row?.applicationName as string) ?? "",
      version:
        (row?.version as string) ??
        (row?.applicationVersionName as string) ??
        "",
      cluster: (row?.cluster as string) ?? (row?.clusterName as string) ?? "",
      tenant: (row?.tenant as string) ?? (row?.tenantName as string) ?? "",
      status: (row?.status as string) ?? "",
    } satisfies ICompanyAppMapping;
  });
}

export function formatCompanyAppMappingLabel(row: ICompanyAppMapping): string {
  const application = row.application?.trim() || "Application";
  const version = row.version?.trim();
  const name =
    version && version !== "—" ? `${application} - ${version}` : application;
  const cloud = row.cloudProvider?.trim();
  const region = row.region?.trim();
  if (cloud || region) {
    return [name, cloud, region].filter(Boolean).join(" · ");
  }
  return name;
}

/** Select options for Application Group Mapping (same rows as Applications tab). */
export function buildApplicationMappingSelectOptions(
  rows: ICompanyAppMapping[],
): ICompanyApplicationMappingOption[] {
  const seen = new Set<string>();
  const options: ICompanyApplicationMappingOption[] = [];

  for (const row of rows) {
    const appMapId = row.id?.trim();
    if (!appMapId || seen.has(appMapId)) continue;
    seen.add(appMapId);
    options.push({
      appMapId,
      label: formatCompanyAppMappingLabel(row),
    });
  }

  return options.sort((a, b) => a.label.localeCompare(b.label));
}
